<script setup>
import { ref, watch } from 'vue';

// 1. Assign props
const props = defineProps({
  userLocation: {
    type: String,
    default: 'London'
  }
});

// 2. Reactive State
const weatherData = ref(null);
const loading = ref(false);
const errorMessage = ref('');

// 3. Weather Icon Mapper
const getWeatherIcon = (code) => {
  if (code === 0) return { icon: 'bi-sun-fill', text: 'Clear' };
  if ([1, 2].includes(code)) return { icon: 'bi-cloud-sun-fill', text: 'Partly Cloudy' };
  if (code === 3) return { icon: 'bi-cloud-fill', text: 'Overcast' };
  if ([45, 48].includes(code)) return { icon: 'bi-cloud-fog2-fill', text: 'Foggy' };
  if ([51, 53, 55, 56, 57].includes(code)) return { icon: 'bi-cloud-drizzle-fill', text: 'Drizzle' };
  if ([61, 63, 65, 66, 67].includes(code)) return { icon: 'bi-cloud-rain-fill', text: 'Rain' };
  if ([71, 73, 75, 77].includes(code)) return { icon: 'bi-snowflake', text: 'Snow' };
  if ([80, 81, 82].includes(code)) return { icon: 'bi-cloud-rain-heavy-fill', text: 'Showers' };
  if ([85, 86].includes(code)) return { icon: 'bi-cloud-snow-fill', text: 'Snow Showers' };
  if ([95, 96, 99].includes(code)) return { icon: 'bi-cloud-lightning-rain-fill', text: 'Thunderstorm' };
  return { icon: 'bi-cloud-fill', text: 'Unknown' };
};

// 4. Format Day Names
const formatDay = (dateString, index) => {
  if (index === 0) return 'Today';
  return new Date(dateString).toLocaleDateString('en-GB', { weekday: 'short' });
};

// 5. Fetch Logic
const fetchUKWeather = async () => {
  const input = props.userLocation ? props.userLocation.trim() : '';
  if (!input) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    let lat, lon, displayName;
    const isPostcode = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i.test(input);

    if (isPostcode) {
      const pcRes = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(input)}`);
      const pcData = await pcRes.json();
      if (pcData.status !== 200) throw new Error('Invalid UK Postcode');
      lat = pcData.result.latitude;
      lon = pcData.result.longitude;
      displayName = `${pcData.result.outward_code}, ${pcData.result.admin_district}`;
    } else {
      const geoRes = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=5&language=en&format=json`);
      const geoData = await geoRes.json();
      if (!geoData.results || geoData.results.length === 0) throw new Error('Town or location not found');

      const ukMatch = geoData.results.find(place => place.country_code === 'GB') || geoData.results[0];
      lat = ukMatch.latitude;
      lon = ukMatch.longitude;
      displayName = `${ukMatch.name}${ukMatch.admin1 ? ', ' + ukMatch.admin1 : ''}`;
    }

    const weatherRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto`);
    if (!weatherRes.ok) throw new Error('Failed to fetch weather data');
    
    const data = await weatherRes.json();

    if (!data.current || !data.daily) {
      throw new Error('Weather data unavailable');
    }

    weatherData.value = {
      location: displayName,
      currentTemp: Math.round(data.current.temperature_2m),
      currentIcon: getWeatherIcon(data.current.weather_code),
      forecast: data.daily.time.slice(0, 5).map((time, i) => ({
        day: formatDay(time, i),
        maxTemp: Math.round(data.daily.temperature_2m_max[i]),
        minTemp: Math.round(data.daily.temperature_2m_min[i]),
        icon: getWeatherIcon(data.daily.weather_code[i])
      }))
    };
  } catch (err) {
    weatherData.value = null;
    errorMessage.value = err.message || 'Error fetching weather data';
  } finally {
    loading.value = false;
  }
};

// 6. Reactive Watcher
watch(
  () => props.userLocation,
  (newVal) => {
    if (newVal) fetchUKWeather();
  },
  { immediate: true }
);
</script>

<template>
  <div class="weather-card">
    <!-- Loading State -->
    <div v-if="loading" class="state-message">
      Loading weather for {{ userLocation }}...
    </div>

    <!-- Error State -->
    <div v-else-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <!-- Weather Data Render -->
    <div v-else-if="weatherData" class="weather-info">
      <h2 class="location">{{ weatherData.location }}</h2>
      
      <div class="current-display">
        <div class="current-icon">
          <i :class="['bi', weatherData.currentIcon.icon]" :title="weatherData.currentIcon.text"></i>
        </div>
        <div class="temp">{{ weatherData.currentTemp }}°C</div>
      </div>

      <div class="forecast-title">5-Day Forecast</div>
      <div class="forecast-grid">
        <div 
          v-for="(day, index) in weatherData.forecast" 
          :key="index" 
          class="forecast-day"
        >
          <span class="day-name">{{ day.day }}</span>
          <i :class="['bi', day.icon.icon]" :title="day.icon.text"></i>
          <span class="temps">{{ day.maxTemp }}° <span class="temp-min">{{ day.minTemp }}°</span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Card Wrapper - Fixed Height & Vertically Centered Content */
.weather-card {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
  width: 100%;
  /* max-width: 420px; */
  height: 300px; /* Fixed height */
  box-sizing: border-box; /* Ensures padding is included inside the 370px */
  
  /* Flexbox settings for vertical & horizontal centering */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  margin: 0 auto;
}

/* Ensure the inner wrapper takes full width for proper grid alignment */
.weather-info {
  width: 100%;
}

/* Status Messages */
.state-message {
  color: #64748b;
  font-size: 0.95rem;
}

.error-message {
  color: #e11d48;
  font-size: 0.95rem;
}

/* Header & Location */
.location {
  font-size: 1.25rem;
  color: #334155;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

/* Current Temp Box */
.current-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin: 0.5rem 0 1rem 0;
}

.current-icon i {
  font-size: 3.25rem;
  color: #0284c7;
}

.temp {
  font-size: 2.75rem;
  font-weight: 700;
  color: #0f172a;
}

/* 5-Day Forecast Grid */
.forecast-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin: 1rem 0 0.5rem 0;
  text-align: left;
}

.forecast-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.forecast-day {
  background: #f8fafc;
  padding: 8px 4px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.day-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
}

.forecast-day i {
  font-size: 1.25rem;
  color: #0284c7;
}

.temps {
  font-size: 0.7rem;
  color: #0f172a;
  font-weight: 600;
}

.temp-min {
  color: #94a3b8;
}
</style>